import { doWarmTransferAction } from "@/repository/queryFns/transferToAgent";
import { useMutation } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import {
	CallForward20Regular,
	PeopleCallFilled,
	PeopleCallRegular,
	PeopleCommunityFilled,
	PeopleCommunityRegular,
	PeopleSwapFilled,
	PeopleSwapRegular,
	PersonDelete20Regular,
} from "@fluentui/react-icons";
import { mergeClasses } from "@fluentui/react-components";
import { WarmTransferActionType } from "@/common/types";
import { useMessage } from "@/common/messages";
import { captureError } from "@/common/helpers/sentryHelper";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { useMemo } from "react";
import { CallInteractionInProgressProps } from "../callInProgressActionButtons/types";

export const CallWarmTransferInProgressActionButtons = (
	props: CallInteractionInProgressProps
) => {
	const { isWarmTransferInProgress = false, isWarmTransferStarted = false } =
		props;

	const style = useInteractionBarCommonStyle();
	const intl = useIntl();
	const { show: showMessage } = useMessage();
	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const warmTransferActionMutation = useMutation({
		mutationFn: doWarmTransferAction,
		onSuccess: (_: any, actionType: any) => {
			if (actionType === WarmTransferActionType.Finalize) {
				showMessage("workspace", {
					intent: "success",
					text: intl.formatMessage({ id: "Transfer.WarmTransferPerformed" }),
				});
			}
		},
		onError: (error: any, actionType: WarmTransferActionType, _: any) => {
			console.error(error);
			captureError(error, "Error on warm transfer action", undefined, {
				warmTransferAction: actionType,
			});
			const errorMessages: Record<WarmTransferActionType, string> = {
				[WarmTransferActionType.Finalize]:
					"Transfer.ErrorMessage.TransferFinalizeFailed",
				[WarmTransferActionType.Cancel]: "Transfer.ErrorMessage.CancelFailed",
				[WarmTransferActionType.ConsultAgent]:
					"Transfer.ErrorMessage.ConsultAgentFailed",
				[WarmTransferActionType.ConsultCaller]:
					"Transfer.ErrorMessage.ConsultCallerFailed",
				[WarmTransferActionType.ConferenceMode]:
					"Transfer.ErrorMessage.ConferenceModeFailed",
			};

			if (errorMessages[actionType]) {
				showMessage("workspace", {
					intent: "error",
					text: intl.formatMessage({ id: errorMessages[actionType] }),
				});
			}
		},
	});

	const isConsultingCaller = useMemo(
		(): boolean =>
			callSession?.state ===
			AgentInteractionStatus.WarmTransferConsultingCaller,
		[callSession?.state]
	);

	const isConsultingAgent = useMemo(
		(): boolean =>
			callSession?.state ===
				AgentInteractionStatus.WarmTransferConsultingAgent ||
			callSession?.state === AgentInteractionStatus.WarmTransferInviteAccepted,
		[callSession?.state]
	);

	const isConferenceMode = useMemo(
		(): boolean =>
			callSession?.state ===
			AgentInteractionStatus.InitiatedWarmTransferConferenceMode,
		[callSession?.state]
	);

	const handleWarmTransferConsultAction = (
		warmTransferConsultAction: WarmTransferActionType
	) => {
		warmTransferActionMutation.mutate(warmTransferConsultAction);
	};

	return (
		<>
			{isWarmTransferInProgress && (
				<>
					<SquareToggleButton
						icon={
							isConsultingCaller ? <PeopleCallFilled /> : <PeopleCallRegular />
						}
						tooltip={intl.formatMessage({
							id: "Interaction.Buttons.ConsultCaller",
						})}
						disabled={isConsultingCaller}
						onClick={() =>
							handleWarmTransferConsultAction(
								WarmTransferActionType.ConsultCaller
							)
						}
					/>
					<SquareToggleButton
						icon={
							isConsultingAgent ? <PeopleSwapFilled /> : <PeopleSwapRegular />
						}
						disabled={isConsultingAgent}
						tooltip={intl.formatMessage({
							id: "Interaction.Buttons.ConsultAgent",
						})}
						onClick={() =>
							handleWarmTransferConsultAction(
								WarmTransferActionType.ConsultAgent
							)
						}
					/>
					<SquareToggleButton
						icon={
							isConferenceMode ? (
								<PeopleCommunityFilled />
							) : (
								<PeopleCommunityRegular />
							)
						}
						disabled={isConferenceMode}
						tooltip={intl.formatMessage({
							id: "Interaction.Buttons.ConferenceMode",
						})}
						onClick={() =>
							handleWarmTransferConsultAction(
								WarmTransferActionType.ConferenceMode
							)
						}
					/>
					<SquareToggleButton
						icon={<CallForward20Regular />}
						tooltip={intl.formatMessage({
							id: "Interaction.Buttons.FinalizeTransfer",
						})}
						className={mergeClasses(
							style.squareColoredButton,
							style.greenColor
						)}
						onClick={() =>
							warmTransferActionMutation.mutate(WarmTransferActionType.Finalize)
						}
					/>
				</>
			)}
			{(isWarmTransferInProgress || isWarmTransferStarted) && (
				<SquareToggleButton
					icon={<PersonDelete20Regular />}
					tooltip={intl.formatMessage({ id: "Common.Disconnect" })}
					className={mergeClasses(style.squareColoredButton, style.redColor)}
					onClick={() =>
						warmTransferActionMutation.mutate(WarmTransferActionType.Cancel)
					}
				/>
			)}
		</>
	);
};
